<template>
    <div
        class="pl-strip"
        role="group"
        :aria-label="ariaLabel"
        @keydown="onKeydown"
    >
        <button
            v-if="looping"
            type="button"
            class="pl-strip__arrow"
            :aria-label="t('player.lobby.stripPrev')"
            @click="rotate(-1)"
        >
            <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>

        <div ref="viewport" class="pl-strip__viewport" @wheel="onWheel">
            <div
                v-if="loading"
                class="pl-strip__track"
                role="status"
                aria-live="polite"
                :aria-label="t('player.common.loading')"
            >
                <span
                    v-for="slot in PENDING_SLOTS"
                    :key="slot"
                    class="pl-mini pl-mini--pending pl-skeleton"
                    :style="{ '--pl-slot': slot }"
                    aria-hidden="true"
                ></span>
            </div>

            <div v-else class="pl-strip__track">
                <button
                    v-for="slot in ring"
                    :key="slot.key"
                    type="button"
                    class="pl-mini"
                    :class="{ 'is-on': slot.item.id === selectedId, 'is-marked': slot.item.id === markedId }"
                    :style="{ '--pl-slot': slot.offset }"
                    :tabindex="slot.reachable ? 0 : -1"
                    :aria-hidden="slot.reachable ? null : 'true'"
                    :aria-label="label(slot.item)"
                    @pointerenter="$emit('hover', slot.item.id)"
                    @pointerleave="$emit('hover', null)"
                    @focus="$emit('hover', slot.item.id)"
                    @blur="$emit('hover', null)"
                    @click="$emit('select', slot.item.id)"
                >
                    <slot :item="slot.item"></slot>
                </button>
            </div>
        </div>

        <button
            v-if="looping"
            type="button"
            class="pl-strip__arrow"
            :aria-label="t('player.lobby.stripNext')"
            @click="rotate(1)"
        >
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
    </div>
</template>

<script setup>
/*
 * One line of cards that never wraps, never shows a scrollbar and, when there
 * are more cards than the window holds, never ends. There is no scroll
 * container at all: each card is placed by a signed slot index (--pl-slot) off
 * the centre, so rotating the ring changes every card's index and the browser
 * animates the trip. That is why nothing here can be scrolled past, and why
 * the page has no axis to scroll on.
 *
 * The strip owns the ring and the geometry only. What a card looks like — a
 * campaign banner, a character portrait — is the caller's slot, and how wide
 * it is belongs to the caller's own --pl-mini-* block in the stylesheet.
 */
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';

const PENDING_SLOTS = [-2, -1, 0, 1, 2];

const props = defineProps({
    items: {type: Array, default: () => []},
    /* The card the caller considers chosen: raised and accented. */
    selectedId: {type: String, default: null},
    /* A second, quieter state — the campaign already joined, say. */
    markedId: {type: String, default: null},
    /* Which card the ring should open on. The ring renders only the slots
       around its cursor, so a caller that arrives with something already
       chosen — a creation draft restored from the last visit — has to be able
       to say so, or the chosen card is not in the DOM at all and the strip
       shows no selection. Null keeps the old behaviour: open at the start. */
    openAtId: {type: String, default: null},
    label: {type: Function, default: (item) => item?.name ?? ''},
    ariaLabel: {type: String, default: ''},
    loading: {type: Boolean, default: false},
});

defineEmits(['hover', 'select']);

const {t} = useI18n();

/* Where the ring rests when it is (re)built. */
function homeCursor() {
    const index = props.items.findIndex((item) => item.id === props.openAtId);
    return index < 0 ? 0 : index;
}

const cursor = ref(0);
const capacity = ref(5);
const half = computed(() => Math.floor(capacity.value / 2) + 1);
const viewport = ref(null);

let resizeObserver = null;
let wheelLockedUntil = 0;

/* A line shorter than the window it stands in has nothing to rotate: the cards
   are simply centred and the arrows do not appear. Looping below that
   threshold would show the same card twice on one screen, which reads as
   duplicated data rather than as a ring. */
const looping = computed(() => props.items.length > capacity.value);

function wrapped(index) {
    const total = props.items.length;
    return props.items[((index % total) + total) % total];
}

const ring = computed(() => {
    const total = props.items.length;
    if (!total) return [];
    if (!looping.value) {
        return props.items.map((item, index) => ({
            key: item.id,
            item,
            offset: index - (total - 1) / 2,
            reachable: true,
        }));
    }
    const slots = [];
    for (let index = cursor.value - half.value; index <= cursor.value + half.value; index += 1) {
        slots.push({
            /* The absolute ring index, so a card keeps its identity as the ring
               turns and the browser animates it across the strip instead of
               repainting the row. */
            key: index,
            item: wrapped(index),
            offset: index - cursor.value,
            /* Only one copy of an item is in the tab order — a short ring shows
               the same card more than once. */
            reachable: Math.abs(index - cursor.value) < total / 2,
        });
    }
    return slots;
});

function rotate(direction) {
    if (!looping.value) return;
    cursor.value += direction;
}

function onKeydown(event) {
    if (event.key === 'ArrowRight') {
        event.preventDefault();
        rotate(1);
    } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        rotate(-1);
    }
}

/* A trackpad reports a flick as a burst of dozens of events. One step per burst
   is what keeps the ring readable; the lock is the burst, not a throttle. */
function onWheel(event) {
    if (!looping.value) return;
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (!delta) return;
    event.preventDefault();
    const now = performance.now();
    if (now < wheelLockedUntil) return;
    wheelLockedUntil = now + 220;
    rotate(delta > 0 ? 1 : -1);
}

/* How many cards the window holds — the only thing the ring needs measured.
   The pitch is read back off two rendered cards rather than restated here:
   reading --pl-mini-step would mean converting rem to pixels by hand, and
   reading it from the layout cannot disagree with what is on the screen. */
function measure() {
    const element = viewport.value;
    if (!element) return;
    const cards = element.querySelectorAll('.pl-mini');
    if (cards.length < 2) return;
    const step = Math.abs(
        cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left,
    );
    if (!step) return;
    capacity.value = Math.max(3, Math.floor(element.clientWidth / step));
}

watch(() => [props.items.length, props.openAtId], async () => {
    cursor.value = homeCursor();
    await nextTick();
    measure();
});

onMounted(() => {
    cursor.value = homeCursor();
    measure();
    if (typeof ResizeObserver !== 'undefined' && viewport.value) {
        resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(viewport.value);
    }
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
});
</script>

<style scoped>
</style>
